import { ref } from 'vue';
import { defineStore } from 'pinia';
import useAxiosInstance from '@/composables/axiosInstance';
import { Gender, GenderResponse } from '@/interfaces/CBW/GenderResponse';
import { PayrollCompany, PayrollCompanyResponse, PayrollSystemMappingResponse, PayrollSystemMapping } from '@/interfaces/CBW/PayrollDatabaseResponse';
import useUISessionStore from '@/stores/ui-session';

interface State {
  genders: Gender[];
  payrollCompanies: PayrollCompany[];
}

const useHRAdminGenderStore = defineStore({
  id: 'hr-admin-genders',
  state: () => ref<State>({
    genders: [],
    payrollCompanies: [],
  }),
  actions: {
    async init() {
      await this.getPayrollCompanies();
      await this.getGenders();
    },
    async getPayrollCompanies(): Promise<void> {
      try {
        const { data } = await useAxiosInstance.get<PayrollCompanyResponse>('/api/companies');

        this.$state = {
          ...this.$state,
          payrollCompanies: data.results,
        };
      } catch (error: any) {
        console.error(error);
      }
    },
    async getGenders(): Promise<void> {
      try {
        const { data } = await useAxiosInstance.get<GenderResponse>('/api/genders');

        this.$state = {
          ...this.$state,
          genders: data.results,
        };
      } catch (error: any) {
        console.error(error);
      }
    },
    async getPayrollSystemMapping(id: number): Promise<void> {
      try {
        const { data } = await useAxiosInstance.get<PayrollSystemMappingResponse>(`/api/Genders/${id}/PayrollSystemMapping`);

        const genders = this.$state.genders.map(gender => {
          if (gender.id === id) {
            return {
              ...gender,
              payrollSystemMapping: this.payrollCompanies.map(company => {
                const existingCompany = data.results.find(result => result.coId === company.id)
                if (existingCompany) {
                  return existingCompany;
                }
                return {
                  coId: company.id,
                  payrollGenderValue: '',
                }
              }),
            }
          }
          return gender;
        });
        this.$state = {
          ...this.$state,
          genders,
        };
      } catch (error: any) {
        console.error(error);
      }
    },
    async insertPayrollSystemMapping(genderId: number, coId: number, payrollGenderValue: string): Promise<void> {
      try {
        const uiStore = useUISessionStore();

        const { status, data } = await useAxiosInstance.put<PayrollSystemMappingResponse>(`/api/Genders/${genderId}/PayrollSystemMapping`, {
          genderId, //@TODO: Id like this removed from the endpoint, since its pointless, its in the URL.
          createdByEmpCode: uiStore.user.empCode,
          coId,
          payrollGenderValue,
        });
        if (status === 200) {
          const updatedGenders = this.genders.map(gender => {
            if (gender.id === genderId) {
              return {
                ...gender,
                payrollSystemMapping: {
                  ...gender.payrollSystemMapping,
                  ...data,
                } as PayrollSystemMapping[],
              }
            }

            return gender;
          });

          this.$state = {
            ...this.$state,
            genders: updatedGenders,
          }
        }
      } catch (error: any) {
        console.error(error);
      }
    },
    async updatePayrollSystemMapping(genderId: number, id: number, payrollGenderValue: string): Promise<void> {
      try {
        const uiStore = useUISessionStore();

        const { data } = await useAxiosInstance.patch<PayrollSystemMappingResponse>(`/api/Genders/${genderId}/PayrollSystemMapping`, {
          id,
          payrollGenderValue,
          modifiedByEmpCode: uiStore.user.empCode,
        });
      } catch (error: any) {
        console.error(error);
      }
    },
    async deletePayrollSystemMapping(genderId: number, id: number): Promise<void> {
      try {
        const { status } = await useAxiosInstance.delete(`/api/Genders/${genderId}/PayrollSystemMapping/${id}`);
      } catch (error: any) {
        console.error(error);
      }
    },
    async insertGender(gender: string): Promise<null | Gender> {
      try {
        const uiStore = useUISessionStore();

        const { status, data } = await useAxiosInstance.put<Gender>('/api/genders', {
          gender: gender.trim(),
          createdByEmpCode: uiStore.user.empCode,
        });

        if (status === 200) {
          this.$state = {
            ...this.$state,
            genders: [...this.$state.genders, data],
          };
          return data;
        }
      } catch (error: any) {
        console.error(error);
      }
      return null;
    },
    async updateGender(gender: Gender): Promise<Gender | null> {
      try {
        const updatedGender = {
          ...gender,
          description: gender.description.trim()
        }

        const uiStore = useUISessionStore();

        const { status } = await useAxiosInstance.patch<Gender>('/api/genders', {
          id: updatedGender.id,
          gender: updatedGender.description,
          modifiedByEmpCode: uiStore.user.empCode,
        });

        if (status === 200) {
          this.$state = {
            ...this.$state,
            genders: [...this.$state.genders.map((p) => {
              if (p.id === updatedGender.id) {
                return updatedGender;
              }
              return p;
            })],
          };

          return updatedGender;
        }
      } catch (error: any) {
        console.error(error);
      }

      return null;
    },
    async deleteGender(id: number): Promise<void> {
      try {
        const { status } = await useAxiosInstance.delete<Gender>(`/api/genders/${id}`);

        if (status === 200) {
          this.$state = {
            ...this.$state,
            genders: [...this.$state.genders.filter((p) => {
              if (p.id !== id) {
                return p;
              }
            })],
          };
        }
      } catch (error: any) {
        console.error(error);
      }
    },
  },
  getters: {
    constantGenders(): string[] {
      // return ['male', 'female', 'unspecified'];
      return [];
    },
    sortedGenders(state): Gender[] {
      const firstListed = state.genders.filter(g => {
        return this.constantGenders.includes(g.description.toLowerCase());
      }).sort((a: Gender, b: Gender) => {
        const aDesc = a.description.toLowerCase();
        const bDesc = b.description.toLowerCase();
        if (aDesc < bDesc) {
          return -1;
        }
        if (aDesc > bDesc) {
          return 1;
        }
        return 0;
      })

      const remaining = state.genders.filter(g => {
        return !this.constantGenders.includes(g.description.toLowerCase());
      }).sort((a: Gender, b: Gender) => {
        const aDesc = a.description.toLowerCase();
        const bDesc = b.description.toLowerCase();

        if (aDesc < bDesc) {
          return -1;
        }
        if (aDesc > bDesc) {
          return 1;
        }
        return 0;
      });

      return [...firstListed, ...remaining];
    },
  },
});

export default useHRAdminGenderStore;
